import { useEffect, useState } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import { ErrorBox } from "@/components/common";
import { AuthShell } from "@/components/AuthShell";
import { parseApiError } from "@/lib/api/client";
import { CheckCircle2, MailCheck, Loader2 } from "lucide-react";

const API_BASE = (import.meta.env.VITE_API_BASE_URL || "/api/v1").replace(/\/+$/, "");

type State = "verifying" | "ok" | "error";

/** Confirms an email-verification link (``/verify-email?token=…``). The token
 * is the proof, so this page is fully public and stateless: it POSTs the token
 * to the backend and reports the result. */
export function VerifyEmail() {
  const nav = useNavigate();
  const [state, setState] = useState<State>("verifying");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const token = new URLSearchParams(window.location.search).get("token");
    if (!token) {
      setState("error");
      setError("Missing verification token. Please use the link from your email.");
      return;
    }
    (async () => {
      try {
        const r = await fetch(`${API_BASE}/iam/email/verify/confirm/`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ token }),
        });
        if (!r.ok) throw await parseApiError(r);
        setState("ok");
      } catch (e) {
        setState("error");
        setError(e instanceof Error ? e.message : String(e));
      }
    })();
  }, []);

  return (
    <AuthShell
      eyebrow="Account security"
      title="Confirm your email"
      subtitle="Verifying your address secures your account and turns on notifications for runs, approvals and governance alerts."
      bullets={[
        "Proves you own the address you signed up with",
        "Required before sensitive write-back actions",
        "One click — links expire after 3 days",
      ]}
    >
      <div className="mb-6 flex items-center gap-2 text-sm font-medium">
        <MailCheck size={16} className="text-accent" /> Email verification
      </div>

      {state === "verifying" && (
        <div className="flex items-center gap-2 text-sm text-muted">
          <Loader2 size={16} className="animate-spin" /> Verifying your email…
        </div>
      )}

      {state === "ok" && (
        <div>
          <div className="mb-4 flex items-center gap-2 text-good">
            <CheckCircle2 size={20} /> <span className="font-medium">Email verified.</span>
          </div>
          <p className="mb-5 text-sm text-muted">
            Thanks — your email address is confirmed. You're all set.
          </p>
          <button className="btn-primary w-full justify-center py-2.5"
                  onClick={() => nav({ to: "/dashboard" })}>
            Go to your workspace →
          </button>
        </div>
      )}

      {state === "error" && (
        <div>
          <ErrorBox error={error ?? "Verification failed."} />
          <p className="mt-4 text-sm text-muted">
            The link may have expired. Sign in and request a new verification
            email from your account.
          </p>
          <div className="mt-4">
            <Link to="/login" className="btn-primary inline-flex">Sign in</Link>
          </div>
        </div>
      )}
    </AuthShell>
  );
}
